import { IOption } from '@cp/src/components/AutoCompleteSearchBar/interfaces';
import { GenericObject } from '@cp/src/types/CommonConstans';

export enum QuestionType {
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTI_SELECT = 'MULTI_SELECT'
}

export interface GetQcAgentAuditsPayload {
  page?: number;
  size?: number;
  auditStatusFilter?: string[];
}

export interface GetQcTeamLeadAuditsPayload {
  page?: number;
  size?: number;
  qcAgentFilter?: string[];
  auditStatusFilter?: string[];
}

export interface GetCallingAgentAuditsPayload {
  page?: number;
  size?: number;
}

export interface AuditAnswer {
  questionId: number;
  selectedOptionIds: number[];
}

export interface SubmitAuditPayload {
  answers: AuditAnswer[];
}

export interface SubmitQcTeamLeadAuditPayload extends SubmitAuditPayload {
  tlReferenceId: string;
}

export interface QcAgentAudit {
  auditAssignmentReferenceId: string;
  entityId: string;
  dateOfAllocation: string;
  auditStatus: string;
  customerName: string;
  customerReferenceId: string;
  caseEntityId: string;
  qcAgentName: string;
  qcAgentReferenceId: string;
  caseReferenceId: string;
  entityCreatedAt: string;
  businessVertical: string;
}

export interface QcAgentAuditsResponse {
  content: QcAgentAudit[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface AnswerView {
  interactionId: number;
  referenceId: string;
  questionReferenceId: string;
  questionName: string;
  questionText: string;
  answerType: string;
  questionTag: string;
  optionReferenceId: string;
  optionText: string;
  inputAmount?: number;
  inputDate?: string;
  inputText?: string;
  interactionLevel: number;
  hierarchyTag: string;
  metadata?: GenericObject;
}

export interface AuditTrailAnswer {
  questionId: number;
  selectedOptionIds: number[];
}

export interface AuditTrail {
  auditor: string;
  answers: AuditTrailAnswer[];
  createdAt?: string;
}

export interface CallingAgentAuditDetails {
  callType: string;
  callLanguage: string;
  tag: string;
  hierarchyTag: string;
  recordingUrl: string;
  callSummary: CallSummary;
  auditTrail: AuditTrail[];
  auditConfig: GenericObject;
  auditStatus: string;
}

export interface QcAgent {
  agentReferenceId: string;
  agentName: string;
}

export interface AuditStatus {
  status: string;
  displayName: string;
}

export interface QcAgentFiltersResponse {
  userRole: string;
  qcAgents: QcAgent[];
  auditStatuses: AuditStatus[];
}

export interface QcTeamLeadFiltersResponse {
  userRole: string;
  qcAgents: QcAgent[];
  auditStatuses: AuditStatus[];
}

export interface CallAuditState {
  isTableLoading: boolean;
  isFeedbackSubmitting: boolean;
  isSidebarLoading: boolean;
  auditData: GenericObject[];
  qcAgentAudits: QcAgentAudit[];
  callingAgentAudits: GenericObject[];
  totalElements: number;
  currentPage: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  callingAgentAuditDetails: CallingAgentAuditDetails | null;
  qcAgentFilters: QcAgentFiltersResponse | null;
  qcTeamLeadFilters: QcTeamLeadFiltersResponse | null;
  selectedAuditAssignmentReferenceId: string | null;
}

export interface AuditOption {
  option_id: number;
  option_tag: string;
  option_text: string;
}

export interface AuditQuestionConfig {
  question_id: number;
  question_tag: string;
  question_text: string;
  question_type: QuestionType;
  options: AuditOption[];
  mcq: boolean;
}

export interface ApiAuditTrailAnswer {
  question_id: number;
  selected_option_ids: number[];
}

export interface AuditFeedbackFormProps {
  onSubmit?: (answers: AuditAnswer[]) => void;
  readOnly?: boolean;
  isDrawerOpen?: boolean;
}

export interface AnswerView {
  questionName: string;
  optionText: string;
  inputText?: string;
  inputAmount?: number;
  inputDate?: string;
}

export interface CallSummary {
  callDateTime?: string;
  team?: string;
  dpdAtCallDate?: number;
  method?: string;
  callingAgentName?: string;
  callingAgentReportingManagerName?: string;
  auditAgentName?: string;
  agentAuditTimestamp?: string;
  auditTLName?: string;
  tlAuditTimestamp?: string;
  answerViews?: AnswerView[];
}

export enum TdmeAuditTeam {
  INHOUSE_TELE = 'INHOUSE_TELE',
  INHOUSE_CSA = 'INHOUSE_CSA',
  EXTERNAL_CSA_INHOUSE = 'EXTERNAL_CSA_INHOUSE',
  EXTERNAL_CSA_AGENCY = 'EXTERNAL_CSA_AGENCY'
}

export const TDME_AUDIT_TEAMS = Object.values(TdmeAuditTeam);

export interface MultiSelectErrorSectionProps {
  question: AuditQuestionConfig;
  selectedOptions: IOption[];
  onSelectionChange: (options: IOption[]) => void;
}

export interface PreAuditValue {
  id: string;
  label: string;
}

export interface PreAuditValuesProps {
  values: PreAuditValue[];
  maxWidth?: number;
  className?: string;
  sourceOfValues: string;
}

export interface PreAuditValueItem {
  id: string;
  label: string;
}

export interface SingleSelectQuestionsProps {
  questions: AuditQuestionConfig[];
  getSelectedOption: (questionId: number) => string;
  handleSingleSelectChange: (questionId: number, optionId: number) => void;
  isFormDisabled: boolean;
  readOnly: boolean;
}

export interface ViolationQuestionProps {
  question: AuditQuestionConfig;
  getSelectedOption: (questionId: number) => string;
  handleSingleSelectChange: (questionId: number, optionId: number) => void;
  isFormDisabled: boolean;
  readOnly: boolean;
}

export interface FooterProps {
  handleSubmit: () => void;
  isSubmitDisabled: boolean;
}

export interface DetailsPopperProps {
  isTDME?: boolean;
  isDrawerOpen?: boolean;
}

export interface PreAuditValuesSectionProps {
  readOnly: boolean;
}
